/**
 * SchemaPage — Catalogue de données unifié
 * Onglet 1 : Données actives (tables Azure SQL GS-Apps)
 * Onglet 2 : Annuaire SOFI (recherche live via INFORMATION_SCHEMA)
 * Onglet 3 : Transformations ETL (lignée SOFI ↔ Azure — G19)
 */
import { useState, useRef, useMemo, Fragment, lazy, Suspense } from 'react';
import { Database, Search, ChevronDown, ChevronRight, Layers, GitCompare } from 'lucide-react';
import { GsSearchInput, PageHeader } from '@gs/ui';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@gs/auth';
import type { TableDefinition, TableLayer } from '@gs/etl';
import { api } from '../services/api';

const LineagePage = lazy(() => import('./LineagePage'));

// ─── Types ────────────────────────────────────────────────────────

interface CatalogEntry extends TableDefinition {
  rowCount: number | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncRows: number | null;
}

interface CatalogResponse {
  ok: boolean;
  catalog: CatalogEntry[];
  total: number;
}

interface SofiColumn {
  name: string;
  type: string;
  nullable: boolean;
  maxLength: number | null;
  matches: boolean;
}

interface SofiTableResult {
  tableName: string;
  rowCount: number;
  matchType: 'table' | 'column' | 'top' | 'fuzzy';
  columns: SofiColumn[];
}

interface SofiSearchResponse {
  ok: boolean;
  query: string | null;
  results: SofiTableResult[];
  totalTables: number;
}

interface SofiOverviewResponse {
  ok: boolean;
  totalTables: number;
}

interface AzureTableSchemaResponse {
  ok: boolean;
  table: string;
  columns: {
    COLUMN_NAME: string;
    DATA_TYPE: string;
    IS_NULLABLE: string;
    CHARACTER_MAXIMUM_LENGTH: number | null;
    ORDINAL_POSITION: number;
  }[];
}

type Tab = 'active' | 'sofi' | 'lineage';

// ─── Fuzzy (couche 3) ─────────────────────────────────────────────

function stripAccents(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function levenshtein(a: string, b: string): number {
  if (a.length > 50 || b.length > 50) return 99;
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return dp[m][n];
}

function fuzzyScore(query: string, target: string): number | null {
  const q = stripAccents(query);
  const t = stripAccents(target);
  if (t.includes(q)) return 0;
  const dist = levenshtein(q, t);
  const threshold = Math.floor(Math.max(q.length, t.length) * 0.4);
  return dist <= threshold ? dist : null;
}

// ─── Onglet 1 : Données actives (lineage + API catalogue) ──────────

const LAYER_LABELS: Record<string, string> = {
  all: 'Toutes',
  DIM: 'DIM',
  STAGING: 'Staging',
  DIM_ANL: 'DIM ANL',
  FACT: 'Fact',
  METRICS: 'Metrics',
  APP: 'App',
};
const LAYER_COLORS: Record<string, string> = {
  DIM: 'bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  STAGING: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  DIM_ANL: 'bg-purple-50 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  FACT: 'bg-teal-50 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  METRICS: 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  APP: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const VIEW_ORDER: (TableLayer | 'all')[] = ['all', 'DIM', 'STAGING', 'DIM_ANL', 'FACT', 'METRICS', 'APP'];

function ActiveCatalogTab() {
  const { user } = useAuth();
  const [view, setView] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [expandedAzureTable, setExpandedAzureTable] = useState<string | null>(null);

  const { data: azureSchema, isFetching: azureSchemaLoading } = useQuery({
    queryKey: ['azure-table-schema', expandedAzureTable],
    queryFn: () =>
      api.get<AzureTableSchemaResponse>('/api/etl/azure-table-schema', { table: expandedAzureTable! }),
    enabled: !!user && !!expandedAzureTable,
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'catalog'],
    queryFn: async () => {
      const r = await api.get<CatalogResponse>('/api/catalog');
      return r.catalog;
    },
    enabled: !!user,
  });

  const filtered = (data ?? [])
    .filter((t) => view === 'all' || t.layer === view)
    .filter((t) => showInactive || t.isActive)
    .filter(
      (t) =>
        !search ||
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.description.toLowerCase().includes(search.toLowerCase()) ||
        t.source.some((s) => s.toLowerCase().includes(search.toLowerCase()))
    );

  const activeCount = (data ?? []).filter((t) => t.isActive).length;
  const inactiveCount = (data ?? []).length - activeCount;

  if (isError) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
          Impossible de charger le catalogue.{' '}
          <button type="button" className="underline touch-manipulation" onClick={() => void refetch()}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <GsSearchInput
          value={search}
          onChange={setSearch}
          placeholder="Chercher table, source, description..."
          className="w-full max-w-xs"
        />
        {VIEW_ORDER.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={`touch-manipulation rounded-xl px-3 py-1.5 text-sm font-medium transition-colors ${
              view === v
                ? 'bg-brand-500 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {LAYER_LABELS[v] ?? v}
          </button>
        ))}
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded-sm border-slate-300"
          />
          Afficher inactives
        </label>
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400">
        {activeCount} tables actives · {inactiveCount} planifiées · {filtered.length} sur {(data ?? []).length} au filtre
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs dark:border-slate-700 dark:bg-slate-900">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/80">
              <th className="w-8 px-2 py-3" aria-hidden />
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400">Table</th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400">Couche</th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400">Sources</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400">Rows</th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400">Dernière sync</th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                  Chargement du catalogue...
                </td>
              </tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                  Aucune table trouvée
                </td>
              </tr>
            )}
            {filtered.map((table) => (
              <Fragment key={table.name}>
              <tr
                className={`cursor-pointer transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/50 ${!table.isActive ? 'opacity-60' : ''} ${expandedAzureTable === table.name ? 'bg-slate-50/90 dark:bg-slate-800/40' : ''}`}
                onClick={() =>
                  setExpandedAzureTable((prev) => (prev === table.name ? null : table.name))
                }
              >
                <td className="px-2 py-3 align-top">
                  {expandedAzureTable === table.name ? (
                    <ChevronDown size={14} className="text-slate-400" />
                  ) : (
                    <ChevronRight size={14} className="text-slate-400" />
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="font-mono text-xs font-medium text-slate-900 dark:text-slate-100">{table.name}</div>
                  <div className="mt-0.5 max-w-md truncate text-xs text-slate-500 dark:text-slate-400">
                    {table.description}
                  </div>
                  {table.sofiTable ? (
                    <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">← SOFI: {table.sofiTable}</div>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-sm px-2 py-0.5 text-xs font-medium ${LAYER_COLORS[table.layer] ?? LAYER_COLORS.APP}`}
                  >
                    {table.layer}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {table.source.map((s) => (
                      <span
                        key={s}
                        className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-mono text-xs text-slate-900 dark:text-slate-100">
                    {table.rowCount !== null ? table.rowCount.toLocaleString('fr-CA') : '—'}
                  </span>
                  {table.rowsEstimate != null && table.coveragePct != null && table.coveragePct < 100 ? (
                    <div className="text-xs text-amber-600 dark:text-amber-400">{table.coveragePct}% couvert</div>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-400">
                  {table.lastSyncAt ? (
                    new Date(table.lastSyncAt).toLocaleString('fr-CA', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })
                  ) : (
                    <span className="text-slate-400">Jamais</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {!table.isActive ? (
                    <span className="text-xs text-slate-500 dark:text-slate-400">🔵 planifié</span>
                  ) : !table.lastSyncStatus ? (
                    <span className="text-xs text-slate-500 dark:text-slate-400">⏳ jamais exécuté</span>
                  ) : String(table.lastSyncStatus).toLowerCase() === 'ok' ? (
                    <span className="text-xs text-green-600 dark:text-green-400">✅ ok</span>
                  ) : (
                    <span className="text-xs text-red-600 dark:text-red-400">❌ erreur</span>
                  )}
                </td>
              </tr>
              {expandedAzureTable === table.name ? (
                <tr className="bg-slate-50/50 dark:bg-slate-900/30">
                  <td colSpan={7} className="border-t border-slate-100 px-4 py-3 dark:border-slate-800">
                    {azureSchemaLoading ? (
                      <span className="text-xs text-slate-500">Chargement des colonnes Azure…</span>
                    ) : azureSchema?.ok && azureSchema.columns?.length ? (
                      <div>
                        <div className="mb-2 text-chip font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Colonnes Azure SQL ({azureSchema.columns.length})
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {azureSchema.columns.map((col) => (
                            <span
                              key={col.COLUMN_NAME}
                              className="flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-0.5 font-mono text-meta dark:border-amber-800 dark:bg-amber-900/20"
                            >
                              <span className="font-semibold text-amber-800 dark:text-amber-300">{col.COLUMN_NAME}</span>
                              <span className="text-amber-600 dark:text-amber-500">
                                {col.DATA_TYPE}
                                {col.CHARACTER_MAXIMUM_LENGTH != null && col.CHARACTER_MAXIMUM_LENGTH > 0
                                  ? `(${col.CHARACTER_MAXIMUM_LENGTH})`
                                  : ''}
                              </span>
                              <span className="text-chip text-slate-500 dark:text-slate-400">
                                {col.IS_NULLABLE === 'YES' ? 'NULL' : 'NOT NULL'}
                              </span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500">Aucune colonne ou erreur de chargement.</span>
                    )}
                  </td>
                </tr>
              ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Onglet 2 : Annuaire SOFI ─────────────────────────────────────

function SofiTableCard({ result, queryStr }: { result: SofiTableResult; queryStr: string }) {
  const [expanded, setExpanded] = useState(false);

  const highlight = (text: string) => {
    if (!queryStr || result.matchType === 'fuzzy') return text;
    const idx = text.toLowerCase().indexOf(queryStr.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="rounded-sm bg-amber-200 px-0.5 text-amber-900 dark:bg-amber-800/60 dark:text-amber-200">
          {text.slice(idx, idx + queryStr.length)}
        </mark>
        {text.slice(idx + queryStr.length)}
      </>
    );
  };

  const matchingCols = result.columns.filter((c) => c.matches);

  const badgeClass =
    result.matchType === 'fuzzy'
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
      : result.matchType === 'table'
        ? 'bg-brand-100 text-brand-800 dark:bg-brand-900/30 dark:text-brand-300'
        : result.matchType === 'top'
          ? 'bg-slate-100 text-slate-500 dark:bg-slate-800'
          : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';

  const badgeLabel =
    result.matchType === 'table'
      ? '↑ nom'
      : result.matchType === 'top'
        ? 'top'
        : result.matchType === 'fuzzy'
          ? 'fuzzy'
          : `${matchingCols.length} col.`;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full touch-manipulation items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
      >
        {expanded ? (
          <ChevronDown size={14} className="shrink-0 text-slate-400" />
        ) : (
          <ChevronRight size={14} className="shrink-0 text-slate-400" />
        )}

        <span className="flex-1 font-mono text-field font-semibold text-slate-800 dark:text-slate-100">
          {highlight(result.tableName)}
        </span>

        <span className={`shrink-0 rounded-full px-2 py-0.5 text-chip font-medium ${badgeClass}`}>{badgeLabel}</span>

        <span className="w-24 shrink-0 text-right font-mono text-xs text-slate-400 dark:text-slate-500">
          {result.rowCount.toLocaleString('fr-CA')} rows
        </span>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3 dark:border-slate-800">
          {matchingCols.length > 0 && (
            <div className="mb-3">
              <div className="mb-1.5 text-chip font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                Colonnes correspondantes
              </div>
              <div className="flex flex-wrap gap-1.5">
                {matchingCols.map((col) => (
                  <span
                    key={col.name}
                    className="flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-0.5 font-mono text-meta dark:border-amber-800 dark:bg-amber-900/20"
                  >
                    <span className="font-semibold text-amber-800 dark:text-amber-300">{highlight(col.name)}</span>
                    {col.type ? <span className="text-amber-500 dark:text-amber-600">{col.type}</span> : null}
                    {col.maxLength != null && col.maxLength > 0 ? (
                      <span className="text-amber-400">({col.maxLength})</span>
                    ) : null}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="mb-1.5 text-chip font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {result.columns.length} colonnes au total
            </div>
            <div className="flex flex-wrap gap-1">
              {result.columns.map((col) => (
                <span
                  key={col.name}
                  className={`rounded-sm px-1.5 py-0.5 font-mono text-chip ${
                    col.matches
                      ? 'bg-amber-50 font-semibold text-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
                      : 'bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                  }`}
                >
                  {col.name}
                  {col.type ? <span className="ml-1 text-slate-400 dark:text-slate-600">{col.type}</span> : null}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SofiDirectoryTab({ sofiTableCount }: { sofiTableCount?: number }) {
  const { user } = useAuth();
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleInput = (val: string) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQuery(val.trim()), 400);
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['sofi-search', query],
    queryFn: () =>
      api.get<SofiSearchResponse>('/api/etl/sofi-search', query ? { q: query } : undefined),
    staleTime: 0,
    enabled: !!user,
  });

  const { data: metaData } = useQuery({
    queryKey: ['sofi-catalog-meta'],
    queryFn: () =>
      api.get<{ ok: boolean; tables: { name: string; columns: string[] }[]; totalTables: number }>(
        '/api/etl/sofi-catalog-meta'
      ),
    staleTime: 10 * 60 * 1000,
    enabled: !!user,
  });

  const serverResults = data?.results ?? [];
  const totalTables = data?.totalTables ?? 0;

  const fuzzyResults = useMemo(() => {
    if (!query || query.length < 3) return [];
    if ((data?.results?.length ?? 0) > 0) return [];
    if (!metaData?.tables) return [];

    const scored: { name: string; score: number; matchingCols: string[] }[] = [];

    for (const table of metaData.tables) {
      const tableScore = fuzzyScore(query, table.name);
      const matchingCols = table.columns
        .map((col) => ({ col, score: fuzzyScore(query, col) }))
        .filter(({ score }) => score !== null)
        .sort((a, b) => (a.score ?? 0) - (b.score ?? 0))
        .map(({ col }) => col);

      const bestScore =
        tableScore !== null
          ? tableScore
          : matchingCols.length > 0
            ? 10 + (fuzzyScore(query, matchingCols[0]) ?? 99)
            : null;

      if (bestScore !== null) {
        scored.push({ name: table.name, score: bestScore, matchingCols });
      }
    }

    return scored
      .sort((a, b) => a.score - b.score)
      .slice(0, 15)
      .map(({ name, matchingCols }) => ({
        tableName: name,
        rowCount: 0,
        matchType: 'fuzzy' as const,
        columns: matchingCols.map((col) => ({
          name: col,
          type: '',
          nullable: true,
          maxLength: null,
          matches: true,
        })),
      }));
  }, [query, data?.results, metaData]);

  const allResults = [...serverResults, ...fuzzyResults];
  const isFuzzy = serverResults.length === 0 && fuzzyResults.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          placeholder={`Chercher dans ${sofiTableCount ?? '…'} tables SOFI... (ex: projet, facture, employé)`}
          value={searchInput}
          onChange={(e) => handleInput(e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm shadow-xs focus:outline-hidden focus:ring-2 focus:ring-brand-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-brand-700"
        />
        {isFetching ? (
          <div className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        ) : null}
      </div>

      {isFuzzy ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-400">
          Aucun résultat exact — résultats approximatifs pour <strong>&quot;{query}&quot;</strong>
        </div>
      ) : null}

      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
        <span>
          {query
            ? `${allResults.length} table${allResults.length > 1 ? 's' : ''} trouvée${allResults.length > 1 ? 's' : ''} sur ${totalTables} dans SOFI`
            : `${totalTables} tables dans SOFI ERP — top 20 par volume`}
        </span>
        {query && serverResults.length > 0 ? (
          <span className="text-amber-600 dark:text-amber-400">
            {serverResults.filter((r) => r.matchType === 'table').length} par nom ·{' '}
            {serverResults.filter((r) => r.matchType === 'column').length} par colonne
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        {isLoading && (
          <div className="py-8 text-center text-slate-500">Connexion SOFI en cours...</div>
        )}
        {!isLoading && allResults.length === 0 && query && (
          <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center dark:border-slate-700">
            <Search size={24} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">
              Aucune table ni colonne contenant <strong>&quot;{query}&quot;</strong>
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Essayez un terme plus court ou en anglais (SOFI utilise parfois des noms anglais)
            </p>
          </div>
        )}
        {allResults.map((result) => (
          <SofiTableCard key={`${result.tableName}-${result.matchType}`} result={result} queryStr={query} />
        ))}
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────

export function SchemaPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('active');

  const { data: catalog } = useQuery({
    queryKey: ['admin', 'catalog'],
    queryFn: async () => {
      const r = await api.get<CatalogResponse>('/api/catalog');
      return r.catalog;
    },
    enabled: !!user,
  });

  const { data: sofiOverview } = useQuery({
    queryKey: ['sofi-total-count'],
    queryFn: () => api.get<SofiOverviewResponse>('/api/etl/sofi-search'),
    staleTime: 0,
    enabled: !!user,
  });

  const { data: sofiMeta } = useQuery({
    queryKey: ['sofi-catalog-meta'],
    queryFn: () =>
      api.get<{ ok: boolean; tables: { name: string; columns: string[] }[]; totalTables: number }>(
        '/api/etl/sofi-catalog-meta'
      ),
    staleTime: 10 * 60 * 1000,
    enabled: !!user,
  });

  const activeCatalogCount = catalog?.filter((t) => t.isActive).length ?? '—';
  const sofiCountLive =
    typeof sofiOverview?.totalTables === 'number'
      ? sofiOverview.totalTables
      : typeof sofiMeta?.totalTables === 'number'
        ? sofiMeta.totalTables
        : undefined;
  const sofiBadge = sofiCountLive ?? '—';

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Catalogue de données"
        description="Tables actives GS-Apps · Annuaire complet SOFI ERP"
      />

      <div className="flex w-fit gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800/50">
        <button
          type="button"
          onClick={() => setTab('active')}
          className={`flex touch-manipulation items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'active'
              ? 'bg-white text-slate-900 shadow-xs dark:bg-slate-900 dark:text-slate-100'
              : 'text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
          }`}
        >
          <Layers size={14} />
          Données actives
          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-chip font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {activeCatalogCount}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setTab('sofi')}
          className={`flex touch-manipulation items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'sofi'
              ? 'bg-white text-slate-900 shadow-xs dark:bg-slate-900 dark:text-slate-100'
              : 'text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
          }`}
        >
          <Database size={14} />
          Annuaire SOFI
          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-chip font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {sofiBadge}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setTab('lineage')}
          className={`flex touch-manipulation items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'lineage'
              ? 'bg-white text-slate-900 shadow-xs dark:bg-slate-900 dark:text-slate-100'
              : 'text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
          }`}
        >
          <GitCompare size={14} />
          Transformations
        </button>
      </div>

      {tab === 'active' ? (
        <ActiveCatalogTab />
      ) : tab === 'sofi' ? (
        <SofiDirectoryTab sofiTableCount={sofiCountLive} />
      ) : (
        <Suspense
          fallback={
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 shadow-xs dark:border-slate-700 dark:bg-slate-900">
              Chargement des transformations…
            </div>
          }
        >
          <LineagePage embedded />
        </Suspense>
      )}
    </div>
  );
}
